import { useNuxtApp } from '#app'
import { onServerPrefetch, ref } from 'vue'

import type { EnhancedCarouselApiProps } from '@backmarket/http-api/src/api-specs-content/models/enhanced-product-carousel'
import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useSearchProducts } from '../../products/useSearchProducts'

const isRefinedProductsCarousel = (
  c: EnhancedCarouselApiProps['carousels'][number],
): c is EnhancedCarouselApiProps['carousels'][number] & {
  recommendation: { type: 'RefinedProducts'; props: RefinedProductsApiProps }
} => c.recommendation.type === 'RefinedProducts'

export async function useBulkFetchCarousels(
  carousels: EnhancedCarouselApiProps['carousels'],
) {
  const logger = useLogger()
  const displayedCarousels = ref(carousels)

  const nuxtApp = useNuxtApp()
  const currentCarouselId = ref<string>(carousels[0]?.id || '')

  const pending = ref(!nuxtApp.isHydrating)

  const refinedProductsAsyncDatas = carousels
    .filter(isRefinedProductsCarousel)
    .map((carousel) => {
      return {
        carouselId: carousel.id,
        asyncData: useSearchProducts(
          {
            productQuery: carousel.recommendation.props.productQuery,
          },
          // we pass server:false because we will control the fetching with the 'execute' functions inside onServerPrefetch
          { server: false, immediate: import.meta.client },
        ),
      }
    })

  // This function is used to fetch all the RefinedProducts carousels and update the list of carousels to display.
  // It is used in 3 different contexts:
  // 1. On the server side (onServerPrefetch)
  // 2. On the client-side (during hydration)
  // 3. On the client-side (after hydration, on a page navigation e.g.)
  async function fetchAllRefinedProductsAndUpdateDisplayedCarousels() {
    try {
      pending.value = true

      // On the server, we don't use useAsyncData since it's wrapped inside onServerPrefetch
      // useAsyncData is not meant to be called inside lifecycle hooks. It should be called inside components' setup functions or plugins
      // Instead we await all the execute calls
      if (import.meta.server) {
        await Promise.all(
          refinedProductsAsyncDatas.map(({ asyncData }) => asyncData.execute()),
        )
      }

      // On the client, we can use useAsyncData because this will be run inside the setup function directly
      if (import.meta.client) {
        await Promise.all(refinedProductsAsyncDatas.map((d) => d.asyncData))
      }

      const carouselsWithProductsIds = refinedProductsAsyncDatas
        .filter(
          (d) =>
            d.asyncData.data.value?.length &&
            d.asyncData.data.value?.length > 0,
        )
        .map((d) => d.carouselId)

      displayedCarousels.value = carousels.filter((carousel) => {
        if (carousel.recommendation.type === 'Recommendations') return true

        return carouselsWithProductsIds.includes(carousel.id)
      })

      currentCarouselId.value = displayedCarousels.value?.[0]?.id || ''
    } catch (error) {
      logger.error('[EnhancedCarousel] Error when fetching refined products', {
        error: error as Error,
        owners: ['bot-squad-content-front'],
      })
    } finally {
      pending.value = false
    }
  }

  // On the server-side, we make sure to fetch the data and update the list of carousels to display before rendering the component
  onServerPrefetch(async () => {
    await fetchAllRefinedProductsAndUpdateDisplayedCarousels()
  })

  if (import.meta.client) {
    // During hydration, we await the function to avoid hydration mismatch errors
    if (nuxtApp.isHydrating) {
      await fetchAllRefinedProductsAndUpdateDisplayedCarousels()
    } else {
      // Out of hydration, we don't await the function to avoid blocking the navigation to the page
      // In this case, we show a skeleton while the data is being fetched
      fetchAllRefinedProductsAndUpdateDisplayedCarousels()
    }
  }

  return {
    displayedCarousels,
    currentCarouselId,
    pending,
  }
}
